import { debounce } from './debounce';

const MOVING_POSITION = {
  LEFT: -1,
  CENTER: 0,
  RIGHT: 1,
};

const POSITION = {
  LEFT: 0,
  CENTER: 1,
  RIGHT: 2,
};

export class Carousel {
  constructor(props) {
    const initOptions = {
      selector: null,
      mode: 'slide', // fade, slide
      autoplay: true,
      loop: false,
      pause: true,
      pager: true,
      duration: 800,
      delay: 4000,
      responsive: [],
      slidesToShow: 1,
      slidesToScroll: 1,
      ratioToPreview: 1,
      ease: 'ease-out',
      className: '',
      threshold: 60,
      currentBreakpoint: null,
    };
    this.options = Object.assign(initOptions, props);

    this.createCarousel(this.options);
    this.initCarousel(this.options);

    this.handleController();
    this.animateCarousel();

    if (!this.loop) {
      this.handleArrowDisabled();
    }

    if (this.responsive.length) {
      this.responsive.sort((previous, current) => previous.breakpoint - current.breakpoint);
      this.detectMediaQuery();
    }
  }

  set setCarouselProps({
    mode,
    autoplay,
    loop,
    pause,
    pager,
    duration,
    delay,
    responsive,
    slidesToShow,
    slidesToScroll,
    slidesToController,
    ratioToPreview,
    ease,
    className,
    threshold,
    currentBreakpoint,
  }) {
    this.children = this.carousel.children;
    this.mode = mode;
    this.duration = duration;
    this.delay = delay;
    this.responsive = responsive;
    this.slidesToShow = slidesToShow;
    this.ease = ease;
    this.className = className;
    this.loop = loop;
    this.pause = pause;
    this.pager = pager;
    this.slidesToController = slidesToController || 0;
    this.slidesToScroll = this.loop === 'infinite' ? 1 : slidesToScroll;
    this.ratioToPreview = this.loop === 'infinite' ? 1 : ratioToPreview;

    this.column = this.children.length;
    this.columnLastIndex = this.column - 1;
    this.previousIndex = 0;
    this.currentIndex = 1;
    this.currentBreakpoint = currentBreakpoint;
    this.startTime = null;
    this.pauseVisibleState = !autoplay;
    this.controllerVisibleState = this.column > this.slidesToController;
    this.pages = this.calculatePages(this.column, this.slidesToScroll, this.loop);
    this.prevent = false;

    this.threshold = threshold;
  }

  set setCarousel({ className }) {
    const controller = this.wrapper.querySelector(`.${className}__controller`);

    if (this.pager) {
      const pagination = controller.querySelector(`.${className}__pagination`);
      const element = document.createElement('div');

      element.className = `${this.className}__pagination`;
      pagination && controller.removeChild(pagination);
      controller.insertBefore(element, controller.firstElementChild);
    }

    if (this.controllerVisibleState) {
      this.handleDraggable();
      controller.style.visibility = 'visible';
    } else {
      controller.style.visibility = 'hidden';
    }

    [...this.children].forEach((child, i) => {
      child.setAttribute('data-slide-index', i);

      this.setElementStyle = child;

      if (this.pager && i < this.pages) {
        this.setPagination = `<button type="button" class="${className}__pager ${
          i === 0 ? 'pager--active' : ''
        }" data-pager-index="${i}">${i}</button>`;
      }
      this.handlePager();
    });
    this.children[0].classList.add('slide--active');
    this.initCarouselStyle(0, POSITION.LEFT);
  }

  set setPagination(button) {
    const pagination = this.wrapper.querySelector(`.${this.className}__pagination`);
    pagination.insertAdjacentHTML('beforeend', button);
  }

  set setElementStyle(child) {
    const {
      dataset: { slideIndex },
    } = child;

    const slideMode = `
      flex: 1 0 ${(100 / this.slidesToShow) * this.ratioToPreview}%;
    `;

    const fadeMode = `
      z-index: ${child.dataset.slideIndex === '0' ? 10 : 0};
      opacity: ${child.dataset.slideIndex === '0' ? 1 : 0};
      transform: translateX(-${slideIndex * 100}%);
      transition-property: opacity;
      transition-duration: ${this.duration / 1000 + 's'};
      transition-timing-function: ${this.ease};
    `;

    const mixedStyle = child.style.cssText.concat(this.mode === 'slide' ? slideMode : fadeMode);

    child.setAttribute('style', mixedStyle);
  }

  initCarouselStyle(slidePosition, previewPosition = POSITION.CENTER, duration = this.duration, ease = this.ease) {
    const previewWidth = (Math.floor((1 - this.ratioToPreview) * 100) / 2) * previewPosition;

    this.carousel.setAttribute(
      'style',
      `
      visibility: visible;
      display: flex;
      align-items: stretch;
      transform: translate3d(${previewWidth}%, 0, 0) translate3d(${-slidePosition}%, 0, 0);
      transition-duration: ${duration / 1000 + 's'};
      transition-timing-function: ${ease};
      `
    );
  }

  createCarousel({ className, selector, pager, pause }) {
    const wrapper = document.createElement('div');

    wrapper.className = `${className}__wrapper`;
    wrapper.style.overflow = 'hidden';

    this.wrapper = wrapper;
    this.carousel = this.options.selector;

    this.controller = `
      <div class="${className}__controller">
        <div class="${className}__arrows">
          <button class="${className}__prev" type="button">prev</button>
          <button class="${className}__next" type="button">next</button>
        </div>
      </div>
    `;

    this.carousel.parentNode.insertBefore(this.wrapper, this.carousel);
    this.wrapper.appendChild(this.carousel);
    this.wrapper.insertAdjacentHTML('beforeend', this.controller);

    if (pager) {
      this.createPagingElem(className, selector.children.length);
    }
    if (pause) {
      this.createPauseElem(className);
    }
  }

  createPagingElem(className, column) {
    const pages = this.calculatePages(column, this.options.slidesToScroll, this.options.loop);
    const arrows = this.wrapper.querySelector(`.${className}__arrows`);
    const paging = `
      <div class="${className}__index">
        <span class="${className}__currIndex">1</span>/<span class="${className}__totalIndex">${pages}</span>
      </div>
    `;

    arrows.insertAdjacentHTML('afterend', paging);
  }

  createPauseElem(className) {
    const pause = `<button class="${className}__pause" type="button">pause</button>`;
    const prev = this.wrapper.querySelector(`.${className}__prev`);

    prev.insertAdjacentHTML('afterend', pause);
  }

  initCarousel(options) {
    this.setCarouselProps = options;
    this.setCarousel = options;
  }

  calculatePages(column, slidesToScroll, loop) {
    const pages = Math.floor(column / this.options.slidesToShow);

    if (loop === 'infinite') {
      return column;
    }

    return column % slidesToScroll === 0 && slidesToScroll === this.options.slidesToShow
      ? pages
      : pages + Math.ceil((column - pages * this.options.slidesToScroll) / this.options.slidesToShow);
  }

  handleArrowDisabled() {
    const lastPageIndex = this.slidesToShow > 1 ? this.pages : 0;
    const prev = this.wrapper.querySelector(`.${this.className}__prev`);
    const next = this.wrapper.querySelector(`.${this.className}__next`);

    prev.disabled = this.previousIndex <= 0;
    next.disabled = this.currentIndex === lastPageIndex;
  }

  addPauseListener(pause) {
    pause.addEventListener('click', () => {
      this.pauseVisibleState = !this.pauseVisibleState;
      if (this.pauseVisibleState) {
        pause.classList.add('slide--pause');
      } else {
        pause.classList.remove('slide--pause');
        this.animateCarousel();
      }
    });
  }

  handlePager() {
    const buttons = this.wrapper.querySelectorAll('[data-pager-index]');
    buttons.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        const {
          dataset: { pagerIndex },
        } = e.target;
        pagerIndex === this.previousIndex || this.activeSlide(parseInt(pagerIndex));
      });
    });
  }

  handleController() {
    const prev = this.wrapper.querySelector(`.${this.className}__prev`);
    const next = this.wrapper.querySelector(`.${this.className}__next`);
    const pause = this.wrapper.querySelector(`.${this.className}__pause`);

    prev.addEventListener('click', () => this.activeSlide(this.previousIndex - 1));
    next.addEventListener('click', () => this.activeSlide(this.currentIndex));

    if (this.pause) {
      this.addPauseListener(pause);
    }
  }

  handleDraggable() {
    let playDraggableSlide;

    const draggableSlide = (start) => {
      const { pageX, pageY } = start;

      return {
        draggableState: (move) => {
          const deltaX = Math.abs(pageX - move.pageX);
          const deltaY = Math.abs(pageY - move.pageY);

          return (deltaY / deltaX) * 100 < this.threshold;
        },
        activeSlide: (end) => {
          const delta = pageX - end;

          if (Math.abs(delta) > this.threshold) {
            delta > 0 ? this.activeSlide(this.currentIndex) : this.activeSlide(this.previousIndex - 1);
          }
        },
      };
    };

    this.wrapper.addEventListener('touchstart', (e) => {
      const [{ pageX, pageY }] = e.changedTouches;
      playDraggableSlide = draggableSlide({ pageX, pageY });
    });

    this.wrapper.addEventListener('touchmove', (e) => {
      const multipleTouch = e.touches.length > 1;
      const [{ pageX, pageY }] = e.changedTouches;
      const isDraggable = playDraggableSlide && playDraggableSlide.draggableState({ pageX, pageY });

      if (multipleTouch || !isDraggable) {
        playDraggableSlide = null;
      } else {
        e.preventDefault();
      }
    });

    this.wrapper.addEventListener('touchend', (e) => {
      const [{ pageX }] = e.changedTouches;
      if (playDraggableSlide) {
        playDraggableSlide.activeSlide(pageX);
      }
    });

    this.wrapper.addEventListener('dragstart', (e) => {
      const { pageX, pageY } = e;
      playDraggableSlide = draggableSlide({ pageX, pageY });
    });

    this.wrapper.addEventListener('dragend', (e) => playDraggableSlide.activeSlide(e.pageX));
  }

  animateCarousel() {
    const slide = (timestamp) => {
      this.startTime = this.startTime || timestamp;
      if (this.pauseVisibleState) return;
      if (timestamp - this.startTime > this.delay) {
        this.activeSlide();
        this.startTime = null;
      }
      window.requestAnimationFrame(slide);
    };
    window.requestAnimationFrame(slide);
  }

  animatePrevent(fadeTransition) {
    let durationTime = null;

    const prevent = (timestamp) => {
      durationTime = durationTime || timestamp;
      if (timestamp - durationTime > this.duration) {
        this.prevent = false;
        this.startTime = null;

        this.mode === 'fade' && fadeTransition();
      } else window.requestAnimationFrame(prevent);
    };

    window.requestAnimationFrame(prevent);
  }

  activePager(currentIndex) {
    const pager = this.wrapper.querySelectorAll(`.${this.className}__pager`);
    const currentPager = pager[currentIndex - 1];

    [...pager].forEach((item) => {
      item.classList.remove('pager--active');
    });

    currentPager && currentPager.classList.add('pager--active');
  }

  activeSlide(target) {
    if (this.prevent) return;
    this.prevent = true;

    target = target < 0 ? this.columnLastIndex : target;

    this.currentIndex = target !== undefined ? target : this.currentIndex;

    const currentItem = this.children[this.currentIndex];
    const previousItem = this.children[this.previousIndex];

    let fadeTransition;

    if (!currentItem || !previousItem) return;
    previousItem.classList.add('slide--prev');
    previousItem.classList.remove('slide--active');
    currentItem.classList.add('slide--active');

    [...this.children].forEach((item) => item.classList.remove('slide--prev'));

    this.mode === 'slide' && this.loop === 'infinite' && this.activeLoopTransition();
    this.mode === 'slide' && this.loop !== 'infinite' && this.activeSlideTransition();
    if (this.mode === 'fade') {
      fadeTransition = this.activeFadeTransition(previousItem, currentItem);
    }
    this.animatePrevent(fadeTransition);

    this.previousIndex = this.currentIndex;
    this.currentIndex = this.currentIndex >= this.columnLastIndex ? 0 : ++this.currentIndex;

    if (!this.loop) {
      this.handleArrowDisabled();
    }

    if (this.pager) {
      const currIndexElem = this.wrapper.querySelector(`.${this.className}__currIndex`);

      if (currIndexElem) {
        currIndexElem.innerText = this.currentIndex === 0 ? this.column : this.currentIndex;
        this.activePager(currIndexElem.innerText);
      }
    }
  }

  activeSlideTransition() {
    const ratioToPreview = this.ratioToPreview * 100;
    const limit = (this.column / this.slidesToShow - 1) * ratioToPreview;
    const rewind = (this.currentIndex - 1) * this.slidesToScroll + this.slidesToShow >= this.column;
    const reverse = this.currentIndex - this.previousIndex === this.columnLastIndex;

    const currentSlide =
      (this.currentIndex + 1) * this.slidesToScroll < this.column
        ? this.currentIndex * this.slidesToScroll
        : this.column - this.slidesToScroll;

    const slidePosition =
      this.currentIndex === this.columnLastIndex ? limit : (currentSlide / this.slidesToShow) * ratioToPreview;
    const previewPositionLimit = this.currentIndex === 0 ? POSITION.LEFT : POSITION.RIGHT;
    const previewPosition =
      this.currentIndex > 0 && this.currentIndex < this.column - this.slidesToShow
        ? POSITION.CENTER
        : previewPositionLimit;

    const rewindSlidePosition = reverse ? limit : 0;
    const rewindPreviewPosition = reverse ? POSITION.RIGHT : POSITION.LEFT;

    if (rewind) {
      this.currentIndex = reverse ? this.children.length - this.slidesToShow : 0;
      this.initCarouselStyle(rewindSlidePosition, rewindPreviewPosition);
    } else {
      this.initCarouselStyle(slidePosition, previewPosition);
    }
  }

  activeLoopTransition() {
    const limit = this.columnLastIndex / this.slidesToShow;
    const rewind = this.previousIndex === this.columnLastIndex && this.currentIndex === 0;
    const reverse = this.currentIndex - this.previousIndex === this.columnLastIndex;

    const currentSlide = rewind ? this.column : this.currentIndex;
    const slidePosition = (reverse ? -1 : currentSlide) / this.slidesToShow;
    const rewindSlidePosition = reverse ? limit : 0;

    this.changeSlidesPosition(rewind, reverse);
    this.initCarouselStyle(slidePosition * 100);

    if (rewind || reverse) {
      setTimeout(() => {
        for (let i = 0; i < this.slidesToShow; i++) {
          this.moveSlidePosition(i, reverse ? MOVING_POSITION.RIGHT : MOVING_POSITION.CENTER);
        }
        this.moveSlidePosition(this.columnLastIndex, MOVING_POSITION.CENTER);

        this.initCarouselStyle(rewindSlidePosition * 100, POSITION.CENTER, 0);
      }, this.duration);
    }
  }

  changeSlidesPosition(rewind, reverse) {
    const loopTransition = this.currentIndex + this.slidesToShow >= this.column;
    const movingRight = loopTransition ? this.currentIndex + this.slidesToShow - this.column : this.previousIndex;

    if (!rewind) {
      this.moveSlidePosition(this.currentIndex, MOVING_POSITION.CENTER);
    }
    if (reverse) {
      this.moveSlidePosition(this.columnLastIndex, MOVING_POSITION.LEFT);
    }

    if (loopTransition) {
      setTimeout(() => {
        this.moveSlidePosition(movingRight, MOVING_POSITION.RIGHT);
      }, this.duration);
    } else {
      this.moveSlidePosition(this.currentIndex + 1, MOVING_POSITION.INIT);
    }
  }

  moveSlidePosition(index, position) {
    const movingPosition = position * this.column * 100;

    this.carousel.children[index].style.transform = `translate3d(${movingPosition}%, 0, 0)`;
  }

  activeFadeTransition(prevItem, currItem) {
    const previousItem = prevItem;
    const currentItem = currItem;

    currentItem.style.zIndex = 10;
    currentItem.style.opacity = 1;
    currentItem.style.transitionDuration = this.duration / 1000 + 's';

    return () => {
      currentItem.style.zIndex = 1;
      previousItem.style.zIndex = 0;
      previousItem.style.opacity = 0;
      previousItem.style.transitionDuration = '0s';
    };
  }

  detectMediaQuery() {
    const mq = this.responsive.some((res) => {
      const mediaQuery = window.matchMedia(`(max-width: ${res.breakpoint}px)`).matches;

      if (mediaQuery) {
        if (this.currentBreakpoint && this.currentBreakpoint === res.breakpoint) return true;

        res.option.currentBreakpoint = res.breakpoint;
        this.resetCarousel(res.option);
        return true;
      }
    });

    if (!mq && this.currentBreakpoint) {
      this.resetCarousel(this.options);
    }

    window.addEventListener('resize.matchMedia', () => debounce(this.detectMediaQuery.bind(this), 250));
  }

  resetCarousel(option) {
    this.initCarousel({
      ...this.options,
      ...option,
    });
  }

  destroyCarousel() {
    this.options = null;
    window.removeEventListener('resize.matchMedia', this.detectMediaQuery.bind(this));
  }
}
